import { useState, useEffect } from 'react';
import axios from 'axios';
import { UserPlus, Loader2, Send, CheckCircle2, Mail, FlaskConical, RefreshCcw } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';

const API = '/api';

// Test-mode entry point. ABSENT by default — see the comment at the button below. Read
// through a function rather than a module-level const so a test can drive both states;
// webpack still inlines the value at build time.
function testModeUiEnabled() {
  return String(process.env.REACT_APP_HG_TEST_MODE_UI || '').trim() === '1';
}

/** Turn an axios error into something an admin can act on.
 *
 * "Could not create the test invite." cost a round trip: the message was a fallback that fired
 * whenever the response carried no `detail`, which is exactly what an unhandled 500 looks like -
 * FastAPI returns plain "Internal Server Error" text, not JSON. So the one case that most needed
 * describing was the one case that got the least.
 *
 * Handles the three real shapes:
 *   - 4xx raised deliberately  -> detail is a string, show it
 *   - 422 validation           -> detail is an ARRAY of objects; naming the fields is the whole
 *                                 point, and rendering the array raw gives "[object Object]"
 *   - 500 unhandled            -> no detail at all; show the status so it reads as a server
 *                                 fault rather than a rejected input
 *
 * This screen is reachable only by Randall and Grant, so there is nothing to leak by being
 * specific — and a vague message on an admin-only screen is pure cost.
 */
function describeError(e, fallback) {
  if (!e.response) {
    return `${fallback} The request never reached the server (${e.message || 'network error'}).`;
  }
  const { status, statusText, data } = e.response;
  const detail = data?.detail;

  if (typeof detail === 'string' && detail.trim()) return detail;

  if (Array.isArray(detail)) {
    const fields = detail
      .map((d) => {
        const name = Array.isArray(d.loc) ? d.loc[d.loc.length - 1] : d.loc;
        return `${name}: ${d.msg}`;
      })
      .join('; ');
    return `${status} rejected by the server — ${fields}`;
  }

  const body = typeof data === 'string' ? data.slice(0, 200) : '';
  return `${fallback} Server returned ${status} ${statusText || ''}`.trim()
    + (body ? ` — ${body}` : ' (no detail — check the backend logs).');
}

// RE-SIGN an ACTIVE fitter onto a replacement licence, on their EXISTING record (28 Oct 2026 —
// Renea Cowan, replacing a lost demo system). The new-fitter form above cannot do this: an
// address that already belongs to a fitter is refused there, because signing would attach to
// that record and approval would then refuse it. Here the record is PICKED, never inferred
// from an email, and the address is the one on the record — nothing is typed. Approving it
// leaves the record active; the card step stores the new card and activates nothing.
function ResignExisting({ initiatedBy, onSent }) {
  const [fitters, setFitters] = useState(null);
  const [q, setQ] = useState('');
  const [pick, setPick] = useState('');
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  // Optional line for the invite email (why this re-sign). Empty: no paragraph at all.
  const [note, setNote] = useState('');

  // The roster is a convenience for this card; whatever it does, it must never take the
  // new-fitter form above down with it.
  useEffect(() => {
    let live = true;
    (async () => {
      try {
        const r = await axios.get(`${API}/fitters`);
        if (live) setFitters((r?.data?.fitters || []).filter((f) => f.status === 'active' && !f.is_test));
      } catch {
        if (live) setFitters([]);
      }
    })();
    return () => { live = false; };
  }, []);

  const shown = (fitters || []).filter((f) =>
    !q.trim() || `${f.name} ${f.primary_email}`.toLowerCase().includes(q.trim().toLowerCase()));
  const chosen = (fitters || []).find((f) => f.id === pick);

  const send = async () => {
    if (!chosen || busy) return;
    setBusy(true); setError(''); setConfirm(false);
    try {
      const body = { fitter_id: chosen.id, initiated_by: initiatedBy };
      if (note.trim()) body.note = note.trim();
      const r = await axios.post(`${API}/agreements/resign-invite`, body);
      onSent?.({ ...r.data, at: new Date().toLocaleString(), isResign: true });
      setPick(''); setQ(''); setNote('');
    } catch (e) {
      setError(describeError(e, 'Could not send the re-sign invite.'));
    }
    setBusy(false);
  };

  return (
    <div className="pt-6 border-t border-hg-border max-w-xl" data-testid="resign-card">
      <div className="text-[10px] font-sans text-hg-text-dim uppercase tracking-wider">
        Existing active fitter — replacement system
      </div>
      <div className="bg-hg-surface border border-hg-border rounded-sm p-5 space-y-3 mt-2">
        <p className="text-xs text-hg-text-dim font-sans">
          Sends the same Fitter License &amp; Account Agreement to a fitter <strong className="text-hg-text">already on the
          roster</strong>, on their existing record. Approving it keeps them active and emails them a card link; the new card
          replaces the old one. No new record, no welcome packet, no list changes, no re-certification.
        </p>
        <input data-testid="resign-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search active fitters"
          className="w-full bg-hg-bg border border-hg-border rounded-sm px-3 py-2 text-sm text-hg-text font-sans" />
        <select data-testid="resign-pick" value={pick} onChange={(e) => { setPick(e.target.value); setConfirm(false); }}
          className="w-full bg-hg-bg border border-hg-border rounded-sm px-3 py-2 text-sm text-hg-text font-sans">
          <option value="">{fitters === null ? 'Loading…' : 'Choose a fitter'}</option>
          {shown.map((f) => <option key={f.id} value={f.id}>{f.name} — {f.primary_email || 'no email on record'}</option>)}
        </select>
        {chosen && (
          <div>
            <label className="block text-[10px] font-sans text-hg-text-dim uppercase tracking-wider mb-1">Note for the invite (optional)</label>
            <textarea data-testid="resign-note" value={note} onChange={(e) => setNote(e.target.value)} rows={2} maxLength={1000}
              placeholder="A line of your own, shown in the invite after the signing link. Leave empty for none."
              className="w-full bg-hg-bg border border-hg-border rounded-sm px-3 py-2 text-sm text-hg-text font-sans" />
          </div>
        )}
        {error && <div className="text-xs text-red-400 font-sans" data-testid="resign-error">{error}</div>}
        {chosen && !confirm && (
          <button data-testid="resign-send" onClick={() => setConfirm(true)} disabled={busy || !chosen.primary_email}
            className="inline-flex items-center gap-2 px-4 py-2 text-xs font-sans border border-hg-border text-hg-text rounded-sm hover:border-hg-green disabled:opacity-40">
            <RefreshCcw size={13} /> Send re-sign invite…
          </button>
        )}
        {chosen && confirm && (
          <div className="text-xs font-sans text-hg-text space-y-2" data-testid="resign-confirm">
            <div>Send a replacement agreement to <strong>{chosen.name}</strong> at <strong>{chosen.primary_email}</strong>, on their existing record?</div>
            <div className="text-hg-text-dim" data-testid="resign-confirm-note">
              {note.trim() ? <>With your note: &ldquo;{note.trim()}&rdquo;</> : 'No note.'}
            </div>
            <div className="flex gap-2">
              <button data-testid="resign-confirm-yes" onClick={send} disabled={busy}
                className="px-3 py-1.5 bg-hg-green text-hg-text rounded-sm disabled:opacity-40">
                {busy ? <Loader2 size={12} className="animate-spin inline" /> : 'Yes, send it'}
              </button>
              <button data-testid="resign-confirm-no" onClick={() => setConfirm(false)} className="px-3 py-1.5 text-hg-text-dim">Cancel</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default function OnboardFitter() {
  const { user } = useAuth();
  const initiatedBy = user?.email || 'admin';

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [sent, setSent] = useState([]); // recent invites this session
  const [previewBusy, setPreviewBusy] = useState(false);
  const [previewMsg, setPreviewMsg] = useState('');
  const [testConfirm, setTestConfirm] = useState(false);   // two-step, never one click

  // A TEMPLATE CHECK, deliberately not part of onboarding. It was never wired into
  // "Send signing invite" — the two calls have always been separate buttons — but it sat in an
  // adjacent, identically-styled card whose copy said "exactly what a new fitter receives",
  // so clicking it emailed the LAST message in the chain before the first step had happened.
  // Now it is visually separated, names which packet it previews, and says it sends nothing
  // to the fitter.
  const sendPacketPreview = async (which) => {
    if (previewBusy) return;
    setPreviewBusy(which); setPreviewMsg('');
    try {
      const r = await axios.post(`${API}/agreements/welcome-preview?packet=${which}`);
      setPreviewMsg(`[TEST] ${which === 'packet_1' ? 'Getting Started' : 'Welcome Packet'} `
        + `preview sent to ${(r.data.sent_to || []).join(', ')} — check your inbox. `
        + 'No fitter was created and nothing was sent to anyone else.');
    } catch (e) {
      setPreviewMsg(e.response?.data?.detail || 'Could not send the preview.');
    }
    setPreviewBusy(false);
  };

  const valid = name.trim() && /\S+@\S+\.\S+/.test(email.trim());

  // A TEST fitter — a real record, walked end to end, with every outbound email redirected to
  // the allowlist and a clean delete afterwards. Its own route (/agreements/invite/test): the
  // real route REFUSES is_test, so a test record cannot be minted by an accidental flag and a
  // real fitter cannot be created by accidentally clicking this.
  const sendTestInvite = async () => {
    if (!valid || busy) return;
    setBusy(true); setError(''); setTestConfirm(false);
    try {
      const r = await axios.post(`${API}/agreements/invite/test`, {
        name: name.trim(), email: email.trim(), initiated_by: initiatedBy, is_test: true,
      });
      setSent((prev) => [{ ...r.data, at: new Date().toLocaleString(), isTest: true }, ...prev]);
      setName(''); setEmail('');
    } catch (e) {
      setError(describeError(e, 'Could not create the test invite.'));
    }
    setBusy(false);
  };

  const sendInvite = async () => {
    if (!valid || busy) return;
    setBusy(true); setError('');
    try {
      const r = await axios.post(`${API}/agreements/invite`, {
        name: name.trim(), email: email.trim(), initiated_by: initiatedBy,
      });
      setSent((prev) => [{ ...r.data, at: new Date().toLocaleString() }, ...prev]);
      setName(''); setEmail('');
    } catch (e) {
      setError(describeError(e, 'Could not send the invite.'));
    }
    setBusy(false);
  };

  return (
    <div className="space-y-6" data-testid="onboard-fitter-page">
      <div>
        <h1 className="font-display text-2xl lg:text-3xl text-hg-text tracking-wider">ONBOARD A NEW FITTER</h1>
        <p className="text-sm text-hg-text-dim font-sans mt-1">
          Send a new fitter a private link to review &amp; sign their Fitter License &amp; Account Agreement.
          They sign; it comes back to you as <span className="text-hg-green">pending review</span>. Nothing activates automatically.
        </p>
      </div>

      <div className="bg-hg-surface border border-hg-border rounded-sm p-5 space-y-4 max-w-xl">
        <div className="flex items-center gap-2 text-xs font-sans text-hg-text-dim">
          <UserPlus size={14} className="text-hg-green" /> Invite is sent from your account ({initiatedBy}) and logged.
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="block text-[10px] font-sans text-hg-text-dim uppercase tracking-wider mb-1">Fitter name *</label>
            <input data-testid="onboard-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Full name"
              className="w-full bg-hg-bg border border-hg-border rounded-sm px-3 py-2 text-sm text-hg-text font-sans focus:outline-none focus:ring-1 focus:ring-hg-green" />
          </div>
          <div>
            <label className="block text-[10px] font-sans text-hg-text-dim uppercase tracking-wider mb-1">Fitter email *</label>
            <input data-testid="onboard-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="fitter@example.com"
              className="w-full bg-hg-bg border border-hg-border rounded-sm px-3 py-2 text-sm text-hg-text font-sans focus:outline-none focus:ring-1 focus:ring-hg-green" />
          </div>
        </div>
        {error && <div className="text-xs text-red-400 font-sans" data-testid="onboard-error">{error}</div>}
        <button data-testid="onboard-send" onClick={sendInvite} disabled={!valid || busy}
          className="inline-flex items-center gap-2 px-4 py-2 text-xs font-sans bg-hg-green text-hg-text rounded-sm hover:opacity-90 disabled:opacity-40">
          {busy ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />} Send signing invite
        </button>

        {/* TEST WALKTHROUGH — deliberately impossible to mistake for the real button above:
            different colour (purple, used nowhere else in the Hub), the word TEST in the label,
            a dashed border, and a confirmation naming the address before anything is created.
            Purple #6B4E9E on #F4F0FB is 5.83:1; the border #8A72C0 on white is 4.00:1.

            HIDDEN BY DEFAULT. The walkthrough is finished and the entry point is closed, but
            the MACHINERY stays — hg_testmode, the routes, the guards and the tests are what
            stopped two writes to Randall's live record, and CoFitter launch will need them.
            Only this button goes. Set REACT_APP_HG_TEST_MODE_UI=1 to bring it back; the
            backend route is unchanged and still admin-gated, so turning it on is a Railway
            variable and a rebuild, not a code change. See hg_testmode's module docstring. */}
        {testModeUiEnabled() && (
        <div className="pt-3 border-t border-dashed border-hg-border">
          {!testConfirm ? (
            <button
              data-testid="onboard-send-test"
              onClick={() => { setError(''); setTestConfirm(true); }}
              disabled={!valid || busy}
              className="inline-flex items-center gap-2 px-4 py-2 text-xs font-sans rounded-sm disabled:opacity-40"
              style={{ background: '#F4F0FB', border: '1px dashed #8A72C0', color: '#5A3F8C' }}
            >
              <FlaskConical size={13} /> Create a TEST fitter (walkthrough)
            </button>
          ) : (
            <div
              data-testid="onboard-test-confirm"
              className="rounded-sm p-3 space-y-2 text-xs font-sans"
              style={{ background: '#F4F0FB', border: '1px solid #8A72C0', color: '#3F2C63' }}
            >
              <p>
                Create a <b>TEST</b> fitter named <b>{name.trim()}</b>?
              </p>
              <p>
                Every email in this walkthrough goes to <b>randall@henry-griffitts.com</b> — not
                to <b>{email.trim()}</b>. The record is named <b>[TEST]</b>, is excluded from the
                roster, digest and fee reporting, and can be deleted cleanly afterwards.
              </p>
              <div className="flex gap-2 pt-1">
                <button
                  data-testid="onboard-test-confirm-yes"
                  onClick={sendTestInvite}
                  disabled={busy}
                  className="inline-flex items-center gap-2 px-3 py-1.5 rounded-sm disabled:opacity-40"
                  style={{ background: '#5A3F8C', color: '#FFFFFF' }}
                >
                  {busy ? <Loader2 size={13} className="animate-spin" /> : <FlaskConical size={13} />}
                  Yes — create the test fitter
                </button>
                <button
                  data-testid="onboard-test-confirm-no"
                  onClick={() => setTestConfirm(false)}
                  className="px-3 py-1.5 rounded-sm border border-hg-border text-hg-text-dim"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
        )}
      </div>

      <ResignExisting initiatedBy={initiatedBy} onSent={(r) => setSent((prev) => [r, ...prev])} />

      {/* Deliberately separated from the invite card above by a rule and a heading: this is a
          template check, NOT a step in onboarding. Clicking it emails only the Hub admins. */}
      <div className="pt-6 border-t border-hg-border max-w-xl">
        <div className="text-[10px] font-sans text-hg-text-dim uppercase tracking-wider">
          Template check — not part of onboarding
        </div>
        <div className="bg-hg-bg/40 border border-dashed border-hg-border rounded-sm p-5 space-y-3 mt-2">
          <p className="text-xs text-hg-text-dim font-sans">
            Emails a <span className="text-hg-gold">[TEST]</span> copy of a packet to the Hub admins
            ({initiatedBy}) so you can read the template. <strong className="text-hg-text">No fitter
            is created, no record changes, and nothing reaches a fitter.</strong>
          </p>
          <p className="text-xs text-hg-text-dim font-sans">
            Packet 1 goes out when a fitter&apos;s card lands. Packet 2 goes out when they&apos;re
            certified — which is also when their demo system enters the build queue.
          </p>
          <div className="flex flex-wrap gap-2">
            <button data-testid="onboard-preview-packet-1" onClick={() => sendPacketPreview('packet_1')}
              disabled={!!previewBusy}
              className="inline-flex items-center gap-2 px-3 py-2 text-xs font-sans border border-hg-border text-hg-text-dim rounded-sm hover:border-hg-green hover:text-hg-text disabled:opacity-40">
              {previewBusy === 'packet_1' ? <Loader2 size={13} className="animate-spin" /> : <Mail size={13} />}
              [TEST] Packet 1 — Getting Started
            </button>
            <button data-testid="onboard-preview-packet-2" onClick={() => sendPacketPreview('packet_2')}
              disabled={!!previewBusy}
              className="inline-flex items-center gap-2 px-3 py-2 text-xs font-sans border border-hg-border text-hg-text-dim rounded-sm hover:border-hg-green hover:text-hg-text disabled:opacity-40">
              {previewBusy === 'packet_2' ? <Loader2 size={13} className="animate-spin" /> : <Mail size={13} />}
              [TEST] Packet 2 — Welcome Packet
            </button>
          </div>
          {previewMsg && <div className="text-xs font-sans text-hg-text-dim" data-testid="onboard-preview-msg">{previewMsg}</div>}
        </div>
      </div>

      {sent.length > 0 && (
        <div className="space-y-2 max-w-xl" data-testid="onboard-sent-list">
          <div className="text-[10px] font-sans text-hg-text-dim uppercase tracking-wider">Invites sent this session</div>
          {sent.map((s) => (
            <div key={s.agreement_id} className="bg-hg-surface border border-hg-green/30 rounded-sm px-4 py-3 text-xs font-sans text-hg-text">
              <div className="flex items-center gap-1.5 text-hg-green"><CheckCircle2 size={13} /> Invite sent · {s.reference}</div>
              <div className="text-hg-text-dim mt-1 flex items-center gap-1.5"><Mail size={11} /> {s.email} · {s.at}{s.invite_campaign ? ` · campaign ${s.invite_campaign}` : ''}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
